
import { useState, useRef, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

export function useCarouselControls() {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const animationRef = useRef<number | null>(null);
  const animationDuration = useRef<number>(15); // Default animation duration

  useEffect(() => {
    // Cleanup animation frame on unmount
    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const togglePause = () => {
    if (carouselRef.current) {
      const newPausedState = !isPaused;
      setIsPaused(newPausedState);
      carouselRef.current.style.animationPlayState = newPausedState ? 'paused' : 'running';
    }
  };

  const setCarouselSpeed = (tracksLength: number) => {
    // Only start the animation if we have tracks and the component is mounted
    if (tracksLength > 0 && carouselRef.current) {
      // Calculate the true width of all track items (without duplicates)
      const track = carouselRef.current;
      
      // We need to measure the actual width of the first set of tracks
      // The total width divided by 2 (since we duplicate the tracks)
      let totalWidth = 0;
      let firstTrackSetWidth = 0;
      
      if (track.children.length > 0) {
        const totalItems = track.children.length;
        const itemsPerSet = totalItems / 2; // We have 2 sets of the same tracks
        
        // Calculate width of the first set
        for (let i = 0; i < itemsPerSet; i++) {
          if (track.children[i]) {
            // Get the full width including margins
            const computedStyle = window.getComputedStyle(track.children[i] as Element);
            const itemWidth = (track.children[i] as HTMLElement).offsetWidth;
            const marginLeft = parseFloat(computedStyle.marginLeft);
            const marginRight = parseFloat(computedStyle.marginRight);
            
            firstTrackSetWidth += (itemWidth + marginLeft + marginRight);
          }
        }
        
        totalWidth = track.scrollWidth;
      }
      
      // If we couldn't calculate the first set width, use half the total width
      if (firstTrackSetWidth === 0) {
        firstTrackSetWidth = totalWidth / 2;
      }
      
      // Base speed factors - SLOWER ON DESKTOP NOW
      const basePixelsPerSecond = isMobile ? 40 : 20; // Reduced speed on desktop (was 100)
      
      // Calculate duration based on width to maintain consistent speed
      // Use the first track set width for calculation to ensure correct looping
      const durationInSeconds = Math.max(firstTrackSetWidth / basePixelsPerSecond, 8);
      animationDuration.current = durationInSeconds;
      
      // Apply calculated animation duration
      track.style.animation = `carousel ${durationInSeconds}s linear infinite`;
      
      // For debugging purposes
      console.info('Setting carousel animation duration:', durationInSeconds + 's', 'Track width:', firstTrackSetWidth, 'isMobile:', isMobile);
      
      // Set initial animation state based on isPaused
      track.style.animationPlayState = isPaused ? 'paused' : 'running';
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!carouselRef.current) return;
    
    setIsDragging(true);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
    // Pause animation while dragging
    carouselRef.current.style.animationPlayState = 'paused';
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!carouselRef.current) return;
    
    setIsDragging(true);
    setStartX(e.touches[0].pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
    // Pause animation while dragging
    carouselRef.current.style.animationPlayState = 'paused';
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !carouselRef.current) return;
    
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Reduced sensitivity for smoother dragging
    
    // Use requestAnimationFrame for smoother scrolling
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
    }
    
    animationRef.current = requestAnimationFrame(() => {
      if (carouselRef.current) {
        carouselRef.current.scrollLeft = scrollLeft - walk;
      }
    });
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging || !carouselRef.current) return;
    
    const x = e.touches[0].pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Reduced sensitivity for smoother dragging
    
    // Use requestAnimationFrame for smoother scrolling
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
    }
    
    animationRef.current = requestAnimationFrame(() => {
      if (carouselRef.current) {
        carouselRef.current.scrollLeft = scrollLeft - walk;
      }
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    if (carouselRef.current && !isPaused) {
      // Only resume animation if not paused
      carouselRef.current.style.animationPlayState = 'running';
    }
    
    // Cancel any pending animation frames
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  };

  return {
    carouselRef,
    isDragging,
    isPaused,
    setCarouselSpeed,
    togglePause,
    handleMouseDown,
    handleTouchStart,
    handleMouseMove,
    handleTouchMove,
    handleDragEnd
  };
}
